import type { Color, ImageElement, Scene, SceneElement, SceneGroup, TextElement, Transform } from './types.js';
import { planPhotoLayout } from './layout.js';
import { COLLAGE_MAX_SLOTS, COLLAGE_MIN_SLOTS } from './collage.js';

/**
 * Photo collage slots: where the photos of a "Kutija sa slikama" go.
 *
 * A collage is sold by its photo count (15, 24) and printed on a big sheet
 * (A3), so the owner never draws 24 rectangles by hand: Studio's "Photo
 * collage" dialog and an agent's blueprint both hand a small recipe to
 * {@link planCollageLayout} and get the same cells back. Two styles:
 *
 *  - `grid`: every cell the same size — {@link planPhotoLayout}, last row
 *    centred. Quote boxes take the last cells.
 *  - `mosaic`: a unit grid tiled with 2×2 big photos, 2×1 photos (either way
 *    round) and single cells, plus 2×1 quote boxes lying flat. Seeded, so
 *    "Shuffle" is a new seed and the same seed is always the same layout.
 *    It always terminates: every search has a node budget, a failed seed is
 *    retried with the next eight, then with one big photo fewer, and a
 *    mosaic that still finds nothing falls back to the grid.
 *
 * {@link buildCollageVariant} turns the cells into scene layers — a collage
 * folder of upload slots "Slika N" (ranked by size) and quote boxes "Citat N"
 * — and {@link reflowCollage} re-lays an existing folder without changing a
 * single slot id, so bindings, masks and samples survive a rearrangement.
 *
 * Pure: no DOM, no Node, no randomness beyond the recipe's seed.
 */

// ── Recipe and layout ───────────────────────────────────────────────────────

export type CollageStyle = 'grid' | 'mosaic';

/** A rectangle in scene px, top-left origin (unlike `Transform`, which is centred). */
export interface CollageArea {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * How a collage's slots were generated. Stored on the collage folder
 * (`SceneGroup.collage.recipe`) so "Rearrange slots…" and an agent can
 * regenerate the same folder. Lengths are scene px.
 */
export interface CollageRecipe {
  style: CollageStyle;
  /** Photo slots. */
  count: number;
  /** Quote boxes (2×1 cells in a mosaic, ordinary cells in a grid); default 0. */
  quotes?: number;
  /** Mosaic only: 2×2 photos; default `Math.round(count / 8)`. */
  big?: number;
  /** Mosaic only: which of the valid tilings; default 1. */
  seed?: number;
  /** Between cells, scene px. */
  gap: number;
  /** Around the cells, inside `area`, scene px. */
  margin: number;
  /** Where the collage goes; absent = the whole canvas. */
  area?: CollageArea;
}

/** One cell of a planned collage. */
export interface CollageCell {
  kind: 'photo' | 'quote';
  /** Centre-origin, never rotated. */
  transform: Transform;
  /** Size in grid units: 2×2 big photo, 2×1 / 1×2 photo, 2×1 quote, 1×1 single. */
  span: { c: 1 | 2; r: 1 | 2 };
  /** Grid column and row of the cell's top-left unit. */
  col: number;
  row: number;
  /**
   * Photos: 1 = biggest, then top to bottom, left to right (areas within 1 %
   * count as equal). Quotes are numbered on their own, in reading order.
   */
  rank: number;
}

export interface CollageLayout {
  /** The style actually laid: a mosaic that found no tiling comes back as `grid`. */
  style: CollageStyle;
  /** Photos in rank order, then quotes in reading order. */
  cells: CollageCell[];
  cols: number;
  rows: number;
  /** 2×2 photos actually placed (may be fewer than asked when they did not fit). */
  bigUsed: number;
  /** The seed whose tiling was used (the recipe's, or a retry after it). */
  seed: number;
  /** `area` minus `margin`: the rectangle the cells and gaps exactly fill. */
  inner: CollageArea;
}

/** Nodes one tiling search may visit before it gives up on that seed. */
export const COLLAGE_MOSAIC_NODE_BUDGET = 20_000;
/** Seeds tried after the recipe's own before a big photo is dropped. */
export const COLLAGE_MOSAIC_SEED_RETRIES = 8;

const MAX_CELLS = 400;
const MAX_QUOTES = 12;
const MIN_COLS = 1;
const MAX_COLS = 16;
/** A unit cell further from square than 2:1 is only used when nothing better fits. */
const GOOD_ASPECT = Math.log(2);
/** The smallest cell side a layout may have, scene px; below it the recipe is refused. */
const MIN_CELL_PX = 1;

const int = (value: unknown, fallback: number, min: number, max: number): number => {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.trunc(value) : fallback;
  return Math.max(min, Math.min(max, n));
};
const length = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

/**
 * A recipe with every default filled in and every number made sane — what
 * the planner works from and what `buildCollageVariant` stores on the folder.
 */
export function normalizeCollageRecipe(recipe: CollageRecipe): Required<Omit<CollageRecipe, 'area'>> & { area?: CollageArea } {
  const count = int(recipe.count, 0, 0, MAX_CELLS);
  const style: CollageStyle = recipe.style === 'mosaic' ? 'mosaic' : 'grid';
  const out: Required<Omit<CollageRecipe, 'area'>> & { area?: CollageArea } = {
    style,
    count,
    quotes: int(recipe.quotes, 0, 0, MAX_QUOTES),
    big: int(recipe.big, Math.round(count / 8), 0, count),
    seed: int(recipe.seed, 1, -2147483648, 2147483647),
    gap: length(recipe.gap),
    margin: length(recipe.margin),
  };
  const a = recipe.area;
  if (a && [a.x, a.y, a.w, a.h].every((n) => typeof n === 'number' && Number.isFinite(n)) && a.w > 0 && a.h > 0) {
    out.area = { x: a.x, y: a.y, w: a.w, h: a.h };
  }
  return out;
}

/**
 * The cells of a collage on a canvas: `recipe.count` photo cells and
 * `recipe.quotes` quote cells, never overlapping, inside `area` minus
 * `margin`, `gap` apart. A mosaic's cells tile that inner rectangle exactly.
 * Deterministic for a recipe; always returns. A recipe whose margin or gap
 * leaves no room for its cells (a side under 1 px) is refused: the layout
 * comes back with no cells — `collageRecipeError` says why — rather than with
 * degenerate or negative ones.
 */
export function planCollageLayout(recipe: CollageRecipe, canvas: { width: number; height: number }): CollageLayout {
  const r = normalizeCollageRecipe(recipe);
  const area = r.area ?? { x: 0, y: 0, w: Math.max(0, canvas.width), h: Math.max(0, canvas.height) };
  const inner = {
    x: area.x + r.margin,
    y: area.y + r.margin,
    w: Math.max(1, area.w - r.margin * 2),
    h: Math.max(1, area.h - r.margin * 2),
  };
  const none: CollageLayout = { style: r.style, cells: [], cols: 0, rows: 0, bigUsed: 0, seed: r.seed, inner };
  if (r.count + r.quotes <= 0 || area.w <= 0 || area.h <= 0) return none;
  if (area.w - r.margin * 2 < MIN_CELL_PX || area.h - r.margin * 2 < MIN_CELL_PX) return none;
  if (r.style === 'mosaic' && r.count > 0) {
    const mosaic = planMosaic(r.count, r.quotes, r.big, r.seed, r.gap, inner);
    if (mosaic) return mosaic;
  }
  const grid = planGrid(r.count, r.quotes, r.gap, r.margin, area, inner, r.seed);
  return grid.cells.every((c) => c.transform.w >= MIN_CELL_PX && c.transform.h >= MIN_CELL_PX) ? grid : none;
}

/**
 * Refuses a recipe Studio or an agent should not save — the pattern of
 * `photoCollageConfigError`: a style other than grid/mosaic, a photo count
 * outside 2…60, quotes outside 0…12, big photos outside 0…count, a seed that
 * is not a whole int32, a negative or non-numeric gap or margin, an empty
 * area, or a gap and margin that leave no room for the cells on the sheet
 * (`planCollageLayout` then lays nothing). Unset optional numbers take their
 * defaults; unset gap and margin are 0. Null when the recipe lays.
 */
export function collageRecipeError(recipe: unknown, canvas: { width: number; height: number }): string | null {
  if (typeof recipe !== 'object' || recipe === null || Array.isArray(recipe)) return 'collage recipe must be an object';
  const r = recipe as Record<string, unknown>;
  if (r.style !== 'grid' && r.style !== 'mosaic') return 'collage recipe.style must be "grid" or "mosaic"';
  const whole = (value: unknown, min: number, max: number) =>
    typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
  if (!whole(r.count, COLLAGE_MIN_SLOTS, COLLAGE_MAX_SLOTS)) {
    return `collage recipe.count must be a whole number from ${COLLAGE_MIN_SLOTS} to ${COLLAGE_MAX_SLOTS}`;
  }
  const count = r.count as number;
  if (r.quotes != null && !whole(r.quotes, 0, MAX_QUOTES)) return `collage recipe.quotes must be a whole number from 0 to ${MAX_QUOTES}`;
  if (r.big != null && !whole(r.big, 0, count)) return 'collage recipe.big must be a whole number from 0 to the photo count';
  if (r.seed != null && !whole(r.seed, -2147483648, 2147483647)) return 'collage recipe.seed must be a whole 32-bit number';
  for (const key of ['gap', 'margin'] as const) {
    const value = r[key];
    if (value != null && !(typeof value === 'number' && Number.isFinite(value) && value >= 0)) {
      return `collage recipe.${key} must be 0 or more scene px`;
    }
  }
  const a = r.area as Partial<CollageArea> | null | undefined;
  if (a != null) {
    const ok = typeof a === 'object' && [a.x, a.y, a.w, a.h].every((n) => typeof n === 'number' && Number.isFinite(n))
      && (a.w as number) > 0 && (a.h as number) > 0;
    if (!ok) return 'collage recipe.area must be {x, y, w, h} in scene px with w and h above 0';
  }
  const quotes = typeof r.quotes === 'number' ? r.quotes : 0;
  const normalized = normalizeCollageRecipe({ ...(r as unknown as CollageRecipe), gap: (r.gap as number) ?? 0, margin: (r.margin as number) ?? 0 });
  const layout = planCollageLayout(normalized, canvas);
  return layout.cells.length === count + quotes ? null : noRoom(normalized, canvas);
}

function noRoom(r: ReturnType<typeof normalizeCollageRecipe>, canvas: { width: number; height: number }): string {
  const box = r.area ?? { w: canvas.width, h: canvas.height };
  return `collage recipe.gap (${r.gap} px) and margin (${r.margin} px) leave no room for ${r.count + r.quotes} cells on a ${Math.round(box.w)} × ${Math.round(box.h)} px area`;
}

/** Throws when a recipe asked for cells and the planner refused it (see `collageRecipeError`). */
function assertLaid(r: ReturnType<typeof normalizeCollageRecipe>, layout: CollageLayout, canvas: { width: number; height: number }): void {
  if (layout.cells.length === 0 && r.count + r.quotes > 0) throw new Error(noRoom(r, canvas));
}

function planGrid(count: number, quotes: number, gap: number, margin: number, area: CollageArea, inner: CollageArea, seed: number): CollageLayout {
  const short = Math.min(area.w, area.h);
  const boxes = planPhotoLayout(count + quotes, area.w, area.h, { gap: gap / short, margin: margin / short })
    .map((t) => ({ ...t, cx: t.cx + area.x, cy: t.cy + area.y }));
  // Rows are the distinct centre lines, in order.
  const lines: number[] = [];
  for (const t of boxes) if (!lines.some((y) => Math.abs(y - t.cy) < 1e-6)) lines.push(t.cy);
  const seen = new Map<number, number>();
  const placed = boxes.map((t, i) => {
    const row = lines.findIndex((y) => Math.abs(y - t.cy) < 1e-6);
    const col = seen.get(row) ?? 0;
    seen.set(row, col + 1);
    return { kind: (i < count ? 'photo' : 'quote') as CollageCell['kind'], transform: t, span: { c: 1 as const, r: 1 as const }, col, row };
  });
  return {
    style: 'grid',
    cells: ranked(placed),
    cols: seen.get(0) ?? 0,
    rows: lines.length,
    bigUsed: 0,
    seed,
    inner,
  };
}

/**
 * Photo ranks for a set of boxes, by the collage rule: bigger first (areas
 * within 1 % of the biggest count as equal), then the higher box, then the one
 * further left. `ranks[i]` is box i's rank, 1-based. Shared by the planner,
 * the builder and the reflow, so "Slika 1" always names the same cell.
 */
export function rankCollageBoxes(boxes: ReadonlyArray<Pick<Transform, 'cx' | 'cy' | 'w' | 'h'>>): number[] {
  const biggest = Math.max(0, ...boxes.map((b) => b.w * b.h));
  const keyed = boxes.map((b, i) => ({
    i,
    bucket: biggest > 0 ? Math.round((b.w * b.h * 100) / biggest) : 0,
    top: b.cy - b.h / 2,
    left: b.cx - b.w / 2,
  }));
  keyed.sort((a, b) => b.bucket - a.bucket || (Math.abs(a.top - b.top) > 0.5 ? a.top - b.top : 0) || a.left - b.left || a.i - b.i);
  const ranks = new Array<number>(boxes.length);
  keyed.forEach((k, n) => (ranks[k.i] = n + 1));
  return ranks;
}

/**
 * Reading order: rows by the top edge (slots whose tops lie within half the
 * median slot height share a row, so a hand-drawn grid a few pixels off still
 * reads as rows), each row left to right. A 2×2 hero in a mosaic reads with
 * the row its top is in.
 */
export function collageReadingOrder<T extends Pick<SceneElement, 'transform'>>(elements: readonly T[]): T[] {
  if (elements.length < 2) return [...elements];
  const boxes = elements.map((el, i) => ({
    el,
    i,
    top: el.transform.cy - Math.abs(el.transform.h) / 2,
    left: el.transform.cx - Math.abs(el.transform.w) / 2,
  }));
  const heights = elements.map((el) => Math.abs(el.transform.h)).sort((a, b) => a - b);
  const mid = Math.floor(heights.length / 2);
  const median = heights.length % 2 ? heights[mid]! : (heights[mid - 1]! + heights[mid]!) / 2;
  const tolerance = median / 2;
  boxes.sort((a, b) => a.top - b.top || a.left - b.left || a.i - b.i);
  const rows: Array<typeof boxes> = [];
  for (const box of boxes) {
    const row = rows[rows.length - 1];
    if (row && box.top - row[0]!.top <= tolerance) row.push(box);
    else rows.push([box]);
  }
  return rows.flatMap((row) => row.sort((a, b) => a.left - b.left || a.top - b.top || a.i - b.i).map((b) => b.el));
}

/** Reading order (top, then left) for quotes; indices into `boxes`. */
function readingOrder(boxes: ReadonlyArray<Pick<Transform, 'cx' | 'cy' | 'w' | 'h'>>): number[] {
  return boxes
    .map((b, i) => ({ i, top: b.cy - b.h / 2, left: b.cx - b.w / 2 }))
    .sort((a, b) => (Math.abs(a.top - b.top) > 0.5 ? a.top - b.top : 0) || a.left - b.left || a.i - b.i)
    .map((k) => k.i);
}

function ranked(cells: Array<Omit<CollageCell, 'rank'>>): CollageCell[] {
  const photos = cells.filter((c) => c.kind === 'photo');
  const quotes = cells.filter((c) => c.kind === 'quote');
  const ranks = rankCollageBoxes(photos.map((c) => c.transform));
  const out: CollageCell[] = photos.map((c, i) => ({ ...c, rank: ranks[i]! }));
  out.sort((a, b) => a.rank - b.rank);
  readingOrder(quotes.map((c) => c.transform)).forEach((i, n) => out.push({ ...quotes[i]!, rank: n + 1 }));
  return out;
}

// ── Mosaic ──────────────────────────────────────────────────────────────────

/** mulberry32: a tiny seeded PRNG, the same numbers in every JS engine. */
function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface MosaicGrid {
  cols: number;
  rows: number;
  cellW: number;
  cellH: number;
  /** Cells left over after one unit per piece: each turns a single into a 2×1 photo. */
  slack: number;
  aspect: number;
  score: number;
}

/** Every unit grid that can hold the pieces, best first. */
function mosaicGrids(count: number, quotes: number, big: number, gap: number, inner: CollageArea): MosaicGrid[] {
  const units = 4 * big + 2 * quotes + (count - big);
  const grids: MosaicGrid[] = [];
  for (let cols = MIN_COLS; cols <= MAX_COLS; cols++) {
    const rows = Math.ceil(units / cols);
    const slack = cols * rows - units;
    if (slack > count - big) continue;
    if (big > 0 && (rows < 2 || cols < 2 || big > Math.floor(rows / 2) * Math.floor(cols / 2))) continue;
    if (quotes > 0 && (cols < 2 || quotes > rows * Math.floor(cols / 2))) continue;
    // A 2×1 photo needs a neighbour: one column and one row both rule it out.
    if (slack > 0 && cols < 2 && rows < 2) continue;
    const cellW = (inner.w - gap * (cols - 1)) / cols;
    const cellH = (inner.h - gap * (rows - 1)) / rows;
    if (!(cellW >= MIN_CELL_PX && cellH >= MIN_CELL_PX)) continue;
    const aspect = Math.abs(Math.log(cellW / cellH));
    grids.push({ cols, rows, cellW, cellH, slack, aspect, score: aspect + (0.35 * slack) / Math.max(1, count) });
  }
  return grids.sort((a, b) => a.score - b.score || a.cols - b.cols);
}

type PieceKind = 'big' | 'quote' | 'wide' | 'tall' | 'single';
interface Piece {
  kind: PieceKind;
  r: number;
  c: number;
  w: 1 | 2;
  h: 1 | 2;
}
const SIZE: Record<PieceKind, { w: 1 | 2; h: 1 | 2 }> = {
  big: { w: 2, h: 2 },
  quote: { w: 2, h: 1 },
  wide: { w: 2, h: 1 },
  tall: { w: 1, h: 2 },
  single: { w: 1, h: 1 },
};

function planMosaic(count: number, quotes: number, big: number, seed: number, gap: number, inner: CollageArea): CollageLayout | null {
  // Two passes: first only unit grids no worse than 2:1, dropping big photos
  // before accepting ribbon-shaped cells; then anything that fits.
  for (const strict of [true, false]) {
    for (let b = big; b >= 0; b--) {
      const grids = mosaicGrids(count, quotes, b, gap, inner).filter((g) => !strict || g.aspect <= GOOD_ASPECT);
      for (const grid of grids.slice(0, 2)) {
        for (let attempt = 0; attempt <= COLLAGE_MOSAIC_SEED_RETRIES; attempt++) {
          const s = (seed + attempt) | 0;
          const pieces = tileMosaic(grid, count, quotes, b, s);
          if (pieces) return mosaicLayout(grid, pieces, gap, inner, s);
        }
      }
    }
  }
  return null;
}

function mosaicLayout(grid: MosaicGrid, pieces: Piece[], gap: number, inner: CollageArea, seed: number): CollageLayout {
  const cells = pieces.map((p) => {
    const w = p.w * grid.cellW + (p.w - 1) * gap;
    const h = p.h * grid.cellH + (p.h - 1) * gap;
    const x = inner.x + p.c * (grid.cellW + gap);
    const y = inner.y + p.r * (grid.cellH + gap);
    return {
      kind: (p.kind === 'quote' ? 'quote' : 'photo') as CollageCell['kind'],
      transform: { cx: x + w / 2, cy: y + h / 2, w, h },
      span: { c: p.w, r: p.h },
      col: p.c,
      row: p.r,
    };
  });
  return {
    style: 'mosaic',
    cells: ranked(cells),
    cols: grid.cols,
    rows: grid.rows,
    bigUsed: pieces.filter((p) => p.kind === 'big').length,
    seed,
    inner,
  };
}

/**
 * Anchors for `n` pieces of `pw`×`ph` units by farthest-point sampling: the
 * first anywhere, each next one among the positions at least 3/4 as far from
 * every earlier anchor as the farthest one is, and not touching one where
 * that is possible. Big photos spread out instead of clumping, two quotes do
 * not read as one long box, and the seed still decides between good spots.
 */
function sampleAnchors(rand: () => number, grid: MosaicGrid, pw: number, ph: number, n: number, taken: Piece[], kind: PieceKind): Piece[] {
  const out: Piece[] = [];
  const all = [...taken];
  const centre = (p: { r: number; c: number; w: number; h: number }) => ({
    x: (p.c + p.w / 2) * grid.cellW,
    y: (p.r + p.h / 2) * grid.cellH,
  });
  for (let i = 0; i < n; i++) {
    let candidates: Array<{ r: number; c: number; d: number; touches: boolean }> = [];
    for (let r = 0; r + ph <= grid.rows; r++) {
      for (let c = 0; c + pw <= grid.cols; c++) {
        if (all.some((q) => r < q.r + q.h && q.r < r + ph && c < q.c + q.w && q.c < c + pw)) continue;
        const me = centre({ r, c, w: pw, h: ph });
        let d = Infinity;
        for (const q of all) {
          const o = centre(q);
          d = Math.min(d, Math.hypot(me.x - o.x, me.y - o.y));
        }
        const touches = all.some((q) => r <= q.r + q.h && q.r <= r + ph && c <= q.c + q.w && q.c <= c + pw);
        candidates.push({ r, c, d: all.length ? d : 0, touches });
      }
    }
    if (candidates.length === 0) break;
    if (candidates.some((k) => !k.touches)) candidates = candidates.filter((k) => !k.touches);
    const best = Math.max(...candidates.map((k) => k.d));
    const pool = all.length ? candidates.filter((k) => k.d >= best * 0.75 - 1e-9) : candidates;
    const pick = pool[Math.floor(rand() * pool.length)]!;
    const piece: Piece = { kind, r: pick.r, c: pick.c, w: pw as 1 | 2, h: ph as 1 | 2 };
    out.push(piece);
    all.push(piece);
  }
  return out;
}

/**
 * Tiles the unit grid exactly with `big` 2×2 photos, `quotes` 2×1 quotes,
 * `slack` 2×1 photos and singles for the rest. Depth-first on the first empty
 * unit (row-major), trying the anchored piece there first, then a seeded,
 * count-weighted order of single / wide / tall, then a big photo or a quote
 * anywhere as a last resort. Null when the node budget runs out.
 */
function tileMosaic(grid: MosaicGrid, count: number, quotes: number, big: number, seed: number): Piece[] | null {
  const { cols, rows } = grid;
  const rand = prng(seed);
  const cells = cols * rows;
  const owner = new Int16Array(cells).fill(-1);
  const left: Record<'big' | 'quote' | 'dom' | 'single', number> = {
    big,
    quote: quotes,
    dom: grid.slack,
    single: count - big - grid.slack,
  };
  if (left.single < 0) return null;

  const bigAnchors = sampleAnchors(rand, grid, 2, 2, big, [], 'big');
  const quoteAnchors = sampleAnchors(rand, grid, 2, 1, quotes, bigAnchors, 'quote');
  const anchors = [...bigAnchors, ...quoteAnchors];  const anchorAt = new Map<number, Piece>();
  for (const a of anchors) anchorAt.set(a.r * cols + a.c, a);
  /** Anchors whose units are all still empty, by unit they cover. */
  const cover = new Map<number, Piece[]>();
  for (const a of anchors) {
    for (let dr = 0; dr < a.h; dr++) {
      for (let dc = 0; dc < a.w; dc++) {
        const k = (a.r + dr) * cols + a.c + dc;
        cover.set(k, [...(cover.get(k) ?? []), a]);
      }
    }
  }

  const pieces: Piece[] = [];
  let nodes = 0;
  let exhausted = false;

  const fits = (r: number, c: number, w: number, h: number): boolean => {
    if (r + h > rows || c + w > cols) return false;
    for (let dr = 0; dr < h; dr++) for (let dc = 0; dc < w; dc++) if (owner[(r + dr) * cols + c + dc]! >= 0) return false;
    return true;
  };
  const live = (a: Piece): boolean => fits(a.r, a.c, a.w, a.h);
  /** Would this placement break an anchor that is still waiting for its piece? */
  const intrudes = (p: Piece): boolean => {
    for (let dr = 0; dr < p.h; dr++) {
      for (let dc = 0; dc < p.w; dc++) {
        for (const a of cover.get((p.r + dr) * cols + p.c + dc) ?? []) {
          if (a.r === p.r && a.c === p.c && a.kind === p.kind) continue;
          if (live(a)) return true;
        }
      }
    }
    return false;
  };
  const set = (p: Piece, value: number): void => {
    for (let dr = 0; dr < p.h; dr++) for (let dc = 0; dc < p.w; dc++) owner[(p.r + dr) * cols + p.c + dc] = value;
  };
  const bucket = (kind: PieceKind): 'big' | 'quote' | 'dom' | 'single' =>
    kind === 'wide' || kind === 'tall' ? 'dom' : kind;

  /** Cheap dead-end tests over the empty units. */
  const viable = (): boolean => {
    let any2x2 = false;
    let anyWide = false;
    let anyTall = false;
    let isolated = 0;
    for (let k = 0; k < cells; k++) {
      if (owner[k]! >= 0) continue;
      const r = Math.floor(k / cols);
      const c = k % cols;
      const right = c + 1 < cols && owner[k + 1]! < 0;
      const down = r + 1 < rows && owner[k + cols]! < 0;
      const leftFree = c > 0 && owner[k - 1]! < 0;
      const up = r > 0 && owner[k - cols]! < 0;
      if (right) anyWide = true;
      if (down) anyTall = true;
      if (right && down && owner[k + cols + 1]! < 0) any2x2 = true;
      if (!right && !down && !leftFree && !up) isolated++;
    }
    if (left.big > 0 && !any2x2) return false;
    if (left.quote > 0 && !anyWide) return false;
    if (left.dom > 0 && !anyWide && !anyTall) return false;
    return isolated <= left.single;
  };

  const search = (from: number): boolean => {
    if (++nodes > COLLAGE_MOSAIC_NODE_BUDGET) {
      exhausted = true;
      return false;
    }
    let k = from;
    while (k < cells && owner[k]! >= 0) k++;
    if (k >= cells) return left.big === 0 && left.quote === 0 && left.dom === 0 && left.single === 0;
    const r = Math.floor(k / cols);
    const c = k % cols;

    const order: PieceKind[] = [];
    const anchored = anchorAt.get(k);
    if (anchored && live(anchored)) order.push(anchored.kind);
    // single / wide / tall in a seeded order weighted by how many are left.
    const weights: Array<[PieceKind, number]> = [
      ['single', left.single],
      ['wide', left.dom / 2],
      ['tall', left.dom / 2],
    ];
    const pool = weights.filter(([, w]) => w > 0);
    const regular: PieceKind[] = [];
    while (pool.length) {
      const total = pool.reduce((sum, [, w]) => sum + w, 0);
      let x = rand() * total;
      let i = 0;
      while (i < pool.length - 1 && x >= pool[i]![1]) x -= pool[i++]![1];
      regular.push(pool[i]![0]);
      pool.splice(i, 1);
    }
    const asPiece = (kind: PieceKind): Piece => ({ kind, r, c, ...SIZE[kind] });
    order.push(...regular.filter((kind) => !intrudes(asPiece(kind))));
    order.push(...regular.filter((kind) => intrudes(asPiece(kind))));
    for (const kind of ['big', 'quote'] as const) if (!order.includes(kind)) order.push(kind);

    for (const kind of order) {
      const key = bucket(kind);
      if (left[key] <= 0) continue;
      const piece = asPiece(kind);
      if (!fits(piece.r, piece.c, piece.w, piece.h)) continue;
      set(piece, pieces.length);
      pieces.push(piece);
      left[key]--;
      if (viable() && search(k + 1)) return true;
      left[key]++;
      pieces.pop();
      set(piece, -1);
      if (exhausted) return false;
    }
    return false;
  };

  return search(0) ? pieces : null;
}

// ── Scene layers ────────────────────────────────────────────────────────────

/** Default quote placeholder, shown until the shopper types their own. */
export const COLLAGE_QUOTE_DEFAULT = 'Tvoj citat';
/** Default collage folder name. */
export const COLLAGE_FOLDER_NAME = 'Kolaž';
const QUOTE_INSET = 0.08;
const QUOTE_COLOR: Color = { hex: '#222222' };
const SLOT_SHOPPER = { edit: true, move: true, scale: true, rotate: true, remove: false } as const;

/** Rounds a length to 0.1 px (font sizes only; geometry is left exact). */
const tenth = (n: number): number => Math.round(n * 10) / 10;

export interface CollageBuildInput {
  scene: Pick<Scene, 'width' | 'height' | 'elements' | 'groups'>;
  recipe: CollageRecipe;
  /** Layout folder name ("24 slike"); absent or '' = no layout folder (a single-count product). */
  layoutName?: string;
  /** Collage folder name; default "Kolaž". */
  collageName?: string;
  /** An existing folder the new folders nest in. */
  parentId?: string;
  /** Id factory: purpose is 'layout' | 'collage' | `slot:${rank}` | `quote:${n}`. */
  mint: (purpose: string) => string;
  /** Font of the quote boxes; default the scene's first text font. */
  quoteFont?: { fontId: string; color?: Color };
  /** False hides every new layer (every layout variant after the first); default true. */
  visible?: boolean;
  /** z of the first new layer; default one above everything on the scene. */
  zStart?: number;
}

export interface CollageVariant {
  /** The layout folder (when named) and the collage folder, parents first. */
  groups: SceneGroup[];
  /** Slots in rank order, then quote boxes. */
  elements: SceneElement[];
  layout: CollageLayout;
  layoutGroupId?: string;
  collageGroupId: string;
  /** Slot ids in rank order ("Slika 1" first). */
  slotIds: string[];
  quoteIds: string[];
}

function firstFont(scene: Pick<Scene, 'elements'>): string {
  for (const el of scene.elements ?? []) if (el.type === 'text' && el.font?.fontId) return el.font.fontId;
  return '';
}

function topZ(elements: readonly SceneElement[] | undefined): number {
  let z = -Infinity;
  for (const el of elements ?? []) if (Number.isFinite(el.z)) z = Math.max(z, el.z);
  return Number.isFinite(z) ? Math.floor(z) + 1 : 0;
}

function quoteBox(cell: Transform): Transform {
  return { cx: cell.cx, cy: cell.cy, w: cell.w * (1 - QUOTE_INSET * 2), h: cell.h * (1 - QUOTE_INSET * 2) };
}

function quoteFontSizes(cell: Transform): { size: number; minSize: number; maxSize: number } {
  return { size: tenth(cell.h * 0.16), minSize: tenth(cell.h * 0.06), maxSize: tenth(cell.h * 0.16) };
}

function newSlot(id: string, rank: number, cell: CollageCell, z: number, groupId: string, layoutName: string, visible: boolean): ImageElement {
  return {
    id,
    name: `Slika ${rank}`,
    type: 'image',
    z,
    transform: { ...cell.transform },
    source: { kind: 'upload' },
    value: null,
    fit: 'cover',
    shopper: { ...SLOT_SHOPPER },
    groupId,
    ...(layoutName ? { layoutGroup: layoutName } : {}),
    ...(visible ? {} : { visible: false }),
  };
}

function newQuote(
  id: string,
  n: number,
  cell: CollageCell,
  z: number,
  groupId: string,
  layoutName: string,
  visible: boolean,
  font: { fontId: string; color?: Color },
): TextElement {
  return {
    id,
    name: `Citat ${n}`,
    type: 'text',
    z,
    transform: quoteBox(cell.transform),
    value: null,
    default: COLLAGE_QUOTE_DEFAULT,
    multiline: true,
    align: 'center',
    verticalAlign: 'center',
    font: { fontId: font.fontId, ...quoteFontSizes(cell.transform) },
    color: { ...(font.color ?? QUOTE_COLOR) },
    shopper: { edit: true },
    groupId,
    ...(layoutName ? { layoutGroup: layoutName } : {}),
    ...(visible ? {} : { visible: false }),
  };
}

/**
 * One count variant of a collage as new scene layers: an optional layout
 * folder ("24 slike", `layout: true`), inside it a collage folder carrying the
 * recipe, and in that the photo slots "Slika N" (upload images, cover fit,
 * framable, not removable) and quote boxes "Citat N". Nothing is added to the
 * scene — the caller commits `groups` and `elements` as one edit. Throws when
 * the gap and margin leave no room for the cells (check `collageRecipeError`
 * first).
 */
export function buildCollageVariant(input: CollageBuildInput): CollageVariant {
  const recipe = normalizeCollageRecipe(input.recipe);
  const canvas = { width: input.scene.width, height: input.scene.height };
  const layout = planCollageLayout(recipe, canvas);
  assertLaid(recipe, layout, canvas);
  const layoutName = (input.layoutName ?? '').trim();
  const visible = input.visible !== false;
  const groups: SceneGroup[] = [];
  let layoutGroupId: string | undefined;
  if (layoutName) {
    layoutGroupId = input.mint('layout');
    groups.push({ id: layoutGroupId, name: layoutName, layout: true, ...(input.parentId ? { parentId: input.parentId } : {}) });
  }
  const collageGroupId = input.mint('collage');
  const parentId = layoutGroupId ?? input.parentId;
  groups.push({
    id: collageGroupId,
    name: input.collageName?.trim() || COLLAGE_FOLDER_NAME,
    ...(parentId ? { parentId } : {}),
    collage: { recipe },
  });

  let z = typeof input.zStart === 'number' && Number.isFinite(input.zStart) ? input.zStart : topZ(input.scene.elements);
  const font = input.quoteFont ?? { fontId: firstFont(input.scene) };
  const elements: SceneElement[] = [];
  const slotIds: string[] = [];
  const quoteIds: string[] = [];
  for (const cell of layout.cells) {
    if (cell.kind === 'photo') {
      const id = input.mint(`slot:${cell.rank}`);
      slotIds.push(id);
      elements.push(newSlot(id, cell.rank, cell, z++, collageGroupId, layoutName, visible));
    } else {
      const id = input.mint(`quote:${cell.rank}`);
      quoteIds.push(id);
      elements.push(newQuote(id, cell.rank, cell, z++, collageGroupId, layoutName, visible, font));
    }
  }
  return {
    groups,
    elements,
    layout,
    ...(layoutGroupId ? { layoutGroupId } : {}),
    collageGroupId,
    slotIds,
    quoteIds,
  };
}

// ── Reflow ──────────────────────────────────────────────────────────────────

export interface CollageReflow {
  scene: Scene;
  layout: CollageLayout;
  /** Slot ids in the new rank order. */
  slotIds: string[];
  quoteIds: string[];
  /** Layers minted for cells the folder did not have. */
  added: string[];
  /** Layers dropped because the recipe has fewer cells (the smallest slots / last quotes). */
  removed: string[];
}

/** Every folder id at or under `groupId`. */
function folderTree(groups: readonly SceneGroup[], groupId: string): Set<string> {
  const ids = new Set([groupId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of groups) {
      if (g.parentId && ids.has(g.parentId) && !ids.has(g.id)) {
        ids.add(g.id);
        grew = true;
      }
    }
  }
  return ids;
}

const nameNumber = (name: string | undefined, word: string): number | undefined => {
  const match = new RegExp(`^\\s*${word}\\s*(\\d+)\\s*$`, 'iu').exec(name ?? '');
  return match ? Number(match[1]) : undefined;
};

/**
 * Re-lays an existing collage folder by a (new) recipe. The existing slots
 * keep their ids, masks, effects and samples: the cell of rank n goes to the
 * slot that is rank n now (by size, then position). Cells the folder lacks get
 * new slots styled like "Slika 1"; slots beyond the new count are removed
 * from the smallest up. Quote boxes follow the same rule by their number.
 * Slots named "Slika N" are renamed to their new rank; other names stay.
 * Folders are never renamed — `collageProblems` warns when a "24 slike" label
 * no longer matches. Throws when `groupId` is not a folder of the scene, and
 * when the gap and margin leave no room for the cells (rather than dropping
 * every slot).
 */
export function reflowCollage(
  scene: Scene,
  groupId: string,
  recipe: CollageRecipe,
  mint: (purpose: string) => string,
  options: { quoteFont?: { fontId: string; color?: Color } } = {},
): CollageReflow {
  const groups = scene.groups ?? [];
  if (!groups.some((g) => g.id === groupId)) throw new Error(`no folder ${groupId} in the scene`);
  const normalized = normalizeCollageRecipe(recipe);
  const canvas = { width: scene.width, height: scene.height };
  const layout = planCollageLayout(normalized, canvas);
  // A refused recipe would otherwise drop every slot of the folder.
  assertLaid(normalized, layout, canvas);
  const tree = folderTree(groups, groupId);
  const members = (scene.elements ?? []).filter((el) => el.groupId && tree.has(el.groupId));

  const slots = members.filter((el): el is ImageElement => el.type === 'image' && el.source?.kind === 'upload');
  const slotRanks = rankCollageBoxes(slots.map((s) => s.transform));
  const slotsByRank = slots.map((s, i) => ({ s, rank: slotRanks[i]! })).sort((a, b) => a.rank - b.rank).map((x) => x.s);

  const quotes = members.filter((el): el is TextElement => el.type === 'text');
  const quoteOrder = readingOrder(quotes.map((q) => q.transform));
  const quotesByNumber = quoteOrder
    .map((i, n) => ({ q: quotes[i]!, n: nameNumber(quotes[i]!.name, 'Citat') ?? 1000 + n }))
    .sort((a, b) => a.n - b.n)
    .map((x) => x.q);

  const photoCells = layout.cells.filter((c) => c.kind === 'photo');
  const quoteCells = layout.cells.filter((c) => c.kind === 'quote');
  const updates = new Map<string, SceneElement>();
  const additions: SceneElement[] = [];
  const added: string[] = [];
  const removed: string[] = [];
  const slotIds: string[] = [];
  const quoteIds: string[] = [];

  const template = slotsByRank[0];
  const quoteTemplate = quotesByNumber[0];
  const font = options.quoteFont ?? (quoteTemplate ? { fontId: quoteTemplate.font.fontId } : { fontId: firstFont(scene) });
  // A folder with no slot (or no quote) to copy: new layers take the layout
  // tag and visibility of whatever the folder already holds.
  const anyMember = members[0];
  const memberTag = anyMember?.layoutGroup ?? '';
  const memberVisible = anyMember ? anyMember.visible !== false : true;

  photoCells.forEach((cell, i) => {
    const existing = slotsByRank[i];
    if (existing) {
      const renamed = existing.name === undefined || nameNumber(existing.name, 'Slika') !== undefined;
      updates.set(existing.id, {
        ...existing,
        ...(renamed ? { name: `Slika ${cell.rank}` } : {}),
        transform: { ...cell.transform },
      });
      slotIds.push(existing.id);
      return;
    }
    const id = mint(`slot:${cell.rank}`);
    let el: ImageElement;
    if (template) {
      const { crop: _crop, sample: _sample, initialCrop: _initial, ...style } = template;
      void _crop;
      void _sample;
      void _initial;
      el = { ...style, id, name: `Slika ${cell.rank}`, transform: { ...cell.transform }, value: null };
    } else {
      el = newSlot(id, cell.rank, cell, 0, groupId, memberTag, memberVisible);
    }
    additions.push(el);
    added.push(id);
    slotIds.push(id);
  });
  for (const s of slotsByRank.slice(photoCells.length)) removed.push(s.id);

  quoteCells.forEach((cell, i) => {
    const existing = quotesByNumber[i];
    const renamedName = `Citat ${cell.rank}`;
    if (existing) {
      const renamed = existing.name === undefined || nameNumber(existing.name, 'Citat') !== undefined;
      updates.set(existing.id, {
        ...existing,
        ...(renamed ? { name: renamedName } : {}),
        transform: quoteBox(cell.transform),
        font: { ...existing.font, ...quoteFontSizes(cell.transform) },
      });
      quoteIds.push(existing.id);
      return;
    }
    const id = mint(`quote:${cell.rank}`);
    let el: TextElement;
    if (quoteTemplate) {
      el = {
        ...quoteTemplate,
        id,
        name: renamedName,
        value: null,
        transform: quoteBox(cell.transform),
        font: { ...quoteTemplate.font, ...quoteFontSizes(cell.transform) },
      };
    } else {
      el = newQuote(id, cell.rank, cell, 0, groupId, memberTag, memberVisible, font);
    }
    additions.push(el);
    added.push(id);
    quoteIds.push(id);
  });
  for (const q of quotesByNumber.slice(quoteCells.length)) removed.push(q.id);

  // New layers go right above the collage's own top layer; anything painted
  // above the collage moves up to make room, so paint order is kept.
  const gone = new Set(removed);
  const kept = (scene.elements ?? []).filter((el) => !gone.has(el.id)).map((el) => updates.get(el.id) ?? el);
  let elements = kept;
  if (additions.length) {
    const ownZ = members.filter((m) => !gone.has(m.id)).map((m) => m.z).filter(Number.isFinite);
    const base = ownZ.length ? Math.floor(Math.max(...ownZ)) + 1 : topZ(kept);
    const room = additions.length;
    const crowded = kept.some((el) => el.z >= base && el.z < base + room);
    elements = crowded ? kept.map((el) => (el.z >= base ? { ...el, z: el.z + room } : el)) : kept;
    additions.forEach((el, i) => elements.push({ ...el, z: base + i }));
  }

  const nextGroups = groups.map((g) =>
    g.id === groupId ? { ...g, collage: { ...(g.collage ?? {}), recipe: normalized } } : g,
  );
  return {
    scene: { ...scene, elements, groups: nextGroups },
    layout,
    slotIds,
    quoteIds,
    added,
    removed,
  };
}

// ── Serbian counting ────────────────────────────────────────────────────────

/**
 * The accusative for "Dodaj još …": 1 sliku, 3 slike, 5 slika, 21 sliku,
 * 12 slika. Like `photoCountLabel`, the teens always take "slika".
 */
export function photoCountAccusative(n: number): string {
  const whole = Math.trunc(Math.abs(n));
  const tens = whole % 100;
  const ones = whole % 10;
  const teen = tens >= 11 && tens <= 14;
  const form = ones === 1 && !teen ? 'sliku' : ones >= 2 && ones <= 4 && !teen ? 'slike' : 'slika';
  return `${Math.trunc(n)} ${form}`;
}
